/**
 * Whether the thread is allowed to move, and where to put it when it is.
 *
 * **This file exists because of one bug.** Leo was reading a briefing and the chat kept
 * pulling him to the bottom; he lost his place every twenty seconds, which is the poll
 * interval. The cause was not the scrolling — it was the *condition*. The first version
 * kept a `stickToBottom` flag that started `true` and was only ever cleared by pressing
 * "show earlier messages", so scrolling up with a finger — the way anybody actually reads
 * back — left it set, and every arriving message was treated as permission to jump.
 *
 * The rule that replaces it is the only one that survives contact with a reader:
 *
 * > **The thread follows the newest message only while the learner is already at the
 * > newest message.** The instant they scroll away from the bottom it stops, and it does
 * > not start again until they come back, or ask to, or say something themselves.
 *
 * So "am I pinned?" is asked of the scroll position itself, on every scroll event, and
 * never remembered as an intention. There is no state to go stale, and no path where the
 * screen believes it has permission it was not given a moment ago.
 *
 * The functions are pure and take a plain `{ scrollTop, scrollHeight, clientHeight }` so
 * the decisions can be tested without a DOM — see `packages/web/test/chat-scroll.test.ts`.
 * They are the whole of the reading experience and they are worth asserting.
 */

/** As much of an `Element` as any decision here needs. */
export interface ScrollBox {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}

/**
 * How close to the bottom still counts as being at the bottom.
 *
 * Not zero, and the reason is arithmetic rather than generosity: on a device with a
 * fractional device pixel ratio `scrollTop + clientHeight` lands a fraction short of
 * `scrollHeight` even when the scroller is hard against its end, so an exact test reports
 * "they have scrolled up" at rest and the thread then follows nothing, ever. It also
 * absorbs the few pixels of momentum an iOS flick leaves behind.
 *
 * The upper bound on this number is set by the failure it must not cause: it has to stay
 * smaller than the shortest thing somebody could be reading, or a new arrival counts as
 * "still at the bottom" while a whole bubble sits unread below the fold. Around four lines
 * of text is the compromise, and it is deliberately not a proportion of the viewport — on
 * a tall phone that would grow into exactly the number it must stay under.
 */
export const BOTTOM_SLACK_PX = 72;

/**
 * How far from the top a scroll has to get before the next page of history is fetched.
 *
 * Bigger than the slack above, because this one is a *prefetch*: being early costs a
 * request, being late costs the learner hitting the end of the thread and waiting. Roughly
 * a screenful on a phone.
 */
export const OLDER_TRIGGER_PX = 400;

/** How far the bottom of the content sits below the bottom of the visible box. */
export function distanceFromBottom(box: ScrollBox): number {
  return box.scrollHeight - box.scrollTop - box.clientHeight;
}

/**
 * Whether the thread may follow a new message.
 *
 * A thread whose content is shorter than its box has nowhere to scroll, so it is pinned by
 * definition — `distanceFromBottom` is zero or negative there and the comparison gets that
 * right with no special case. It matters on the first day of an account, when the whole
 * conversation is three bubbles: reading that as "scrolled up" would raise a "new messages"
 * badge on a screen where every message is already in view.
 */
export function isPinned(box: ScrollBox, slack: number = BOTTOM_SLACK_PX): boolean {
  return distanceFromBottom(box) <= slack;
}

/** Whether a scroll has reached far enough up to be worth fetching more history. */
export function wantsOlder(box: ScrollBox, trigger: number = OLDER_TRIGGER_PX): boolean {
  return box.scrollTop <= trigger;
}

/**
 * Where to put the scroll after older messages have been put on top of it.
 *
 * **The classic trap, and the one adjacent to the bug this file is named for.** Prepending
 * a page grows the content above the viewport, so a `scrollTop` left alone points at
 * content that has moved down by the height of everything inserted — the learner asked for
 * last week and is thrown a week further back, mid-sentence, which reads exactly like the
 * app losing their place.
 *
 * The fix anchors on `scrollHeight - scrollTop`, the distance from the reader's position to
 * the end of the content, which is the one quantity a prepend does not change: nothing was
 * inserted below them, so putting that number back puts the same words under the same
 * finger. Measure it before the state update, restore it in a layout effect before the
 * browser paints, and there is no visible jump at all.
 *
 * **Deliberately not expressed through `clientHeight`.** `scrollHeight - scrollTop -
 * clientHeight` is the same number by a different route and is the obvious thing to reach
 * for, since it is `distanceFromBottom` and already written above — but it is only
 * equivalent while the box's own height is unchanged, and on this screen it is not: the
 * composer grows a row as somebody types and the thread loses exactly that much height. A
 * restore computed through a stale `clientHeight` misses by the height of a line of text,
 * which is enough to lose the sentence somebody was on.
 *
 * Rejected: CSS scroll anchoring (`overflow-anchor`), which would do this for free — and
 * which Safari does not implement, so on the one platform this app is installed on it is
 * not a fallback but a no-op. Also rejected: summing the inserted rows' heights, which is
 * the same answer computed from more moving parts and is wrong the moment a row reflows
 * after it was measured.
 */
export function distanceFromEnd(box: ScrollBox): number {
  return box.scrollHeight - box.scrollTop;
}

/** Puts back what `distanceFromEnd` measured before the prepend. */
export function restoredScrollTop(box: ScrollBox, keptFromEnd: number): number {
  return Math.max(0, box.scrollHeight - keptFromEnd);
}

/** The scroll position that puts the newest message against the bottom edge. */
export function bottomScrollTop(box: ScrollBox): number {
  return Math.max(0, box.scrollHeight - box.clientHeight);
}

/**
 * How many messages have arrived since the last one the learner had in front of them.
 *
 * Counted by id rather than by length, because the thread is a set union that also grows
 * *upwards* when history is paged in — "how many more rows than last time" would announce
 * forty new messages to somebody who had just asked for last week.
 *
 * The ids are `<when>#<message id>`, which is what the server sorts the log by, so
 * comparing them as strings is comparing them chronologically. That is the same property
 * `merge` in `Chat.tsx` relies on, and it is why neither of them has to parse a date.
 */
export function arrivedSince(messages: readonly { id: string }[], lastSeenId: string): number {
  if (lastSeenId === '') return 0;
  let count = 0;
  for (const message of messages) {
    if (message.id > lastSeenId) count += 1;
  }
  return count;
}
